import { NoSymbolIcon } from '@heroicons/react/16/solid';
import DOMPurify from 'dompurify';
import { isEmpty } from 'lodash';
import { ReactNode, useEffect, useState } from 'react';
import useChatActions from '../../../../../../../Chat.actions';
import {
  EventType,
  MediaType,
  MessageErrorType,
  MessageState,
} from '../../../../../../../Chat.enum.ts';
import { tAttachment } from '../../../../../../../Chat.types';
import { tMessageByContentType } from '../MessageBubble.type';
import EncryptedContent from './EncryptedContent/EncryptedContent.component.tsx';
import MessageByAttachmentType from './MessageByAttachmentType.component';
import { Lightbox } from '../Lightbox/Lightbox.component.tsx';
import {
  isDocumentOrPdf,
  isVideoOrAudio,
} from '../../../../../../MessageBar/components/Thumbnail/Thumbnail.functions.ts';
import {
  ExclamationCircleIcon,
  InformationCircleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import useMessageBubbleMenu from '../../MessageBubbleMenu/MessageBubbleMenu.hook.tsx';
import {
  isLeadsalesTemplateRefusal,
  isUnsupportedContentNotice,
  isWapiUnsupportedInfoNote,
} from '../../../../../../../Chat.utils.ts';
import {
  LLog,
  LOG_COMPONENTS,
  LOG_OPERATIONS,
} from '../../../../../../../../../lib/logging';
import { useTranslation } from 'react-i18next';
import { lookupByMessageErrorType } from '../../../../../../../../../utils/ErrorDirectory';
import WapiInlineErrorCard from './WapiInlineErrorCard/WapiInlineErrorCard.component';
import {
  noticeSourceOf,
  resolveIncomingMetaErrorNotice,
  resolveOutgoingMetaErrorNotice,
} from '../MessageBubbleV2.functions';
import UnavailableFile from './UnavailableFile/UnavailableFile.component';
import { useChatContext } from '../../../../../../../Chat.hook';
import {
  getAttachmentUniqueKey,
  isRenderableAttachment,
} from './MessageByContentType.functions';
import useUnsupportedNoticeAnalytics from 'src/hooks/analytics/chat/useUnsupportedNoticeAnalytics.hook';
import { countedNoticeOf } from 'src/hooks/analytics/chat/unsupportedNoticeAnalytics.functions';

export default function MessageByContentType({
  event,
  textEnd,
}: tMessageByContentType) {
  const [openLightbox, setOpenLightbox] = useState<boolean>(false);
  const [slideIndex, setSlideIndex] = useState<number>(0);
  const { showErrorMessage, renderMessageNotSupported } =
    useMessageBubbleMenu();
  const { selectedChat } = useChatContext();
  const { t } = useTranslation();

  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

  const { decryptAttachment } = useChatActions();

  const [attachments, setAttachments] = useState<tAttachment[]>([]);

  useEffect(() => {
    if (
      !event?.content?.attachments ||
      event.content.attachments.length === 0
    ) {
      return;
    }

    const processedUrls = new Set<string>();
    const newAttachments: tAttachment[] = [];

    const processAttachments = async () => {
      if (!event?.content?.attachments) return;

      for (const attachment of event.content.attachments) {
        try {
          if (attachment?.url && processedUrls.has(attachment.url)) {
            continue;
          }

          const decryptedAttachment = await decryptAttachment(attachment);

          if (isRenderableAttachment(decryptedAttachment)) {
            if (decryptedAttachment.url) {
              processedUrls.add(decryptedAttachment.url);
            }
            newAttachments.push(decryptedAttachment);
          }
        } catch (error) {
          LLog.error(
            {
              component: LOG_COMPONENTS.UI,
              method: 'MessageByContentType.processAttachments',
              operation: LOG_OPERATIONS.ERROR,
            },
            'Error in process attachments:',
            error as unknown as Error,
          );
        }
      }

      if (newAttachments.length > 0) {
        setAttachments((prevAttachments) => {
          const prev = Array.isArray(prevAttachments) ? prevAttachments : [];
          const combined = [...prev, ...newAttachments];
          // Dedupe by content-derived key, keeping the first occurrence — a
          // manual filter rather than lodash uniqBy so getAttachmentUniqueKey
          // receives the index it needs to disambiguate SHARE_POST cards that
          // have no video_id/embed_url.
          const seenKeys = new Set<string>();
          const uniqueAttachments = combined.filter((attachment, index) => {
            const key = getAttachmentUniqueKey(attachment, index);
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
          });

          return uniqueAttachments;
        });
      }
    };

    processAttachments();
  }, [event?.content?.attachments]);

  const renderTextWithLinks = (text: string) => {
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        const href = part.startsWith('www.') ? `http://${part}` : part;
        // Session replay records `href` unmasked; the block class keeps it out.
        return (
          <a
            key={index}
            href={href}
            className="ph-no-capture"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'blue', textDecoration: 'underline' }}
          >
            {part}
          </a>
        );
      }
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(part),
          }}
        />
      );
    });
  };

  // lineEnd closes the last line from inside it: each line is a block, so
  // anything rendered after them would start a line of its own.
  const renderTextWithNewlinesAndLinks = (text: string, lineEnd?: ReactNode) => {
    const lines = text.split('\n');

    return lines.map((line, index) => (
      <div key={index}>
        {renderTextWithLinks(line)}
        {index < lines.length - 1 ? <br /> : lineEnd}
      </div>
    ));
  };

  const isDeletedMessage =
    event?.type === EventType.OUTBOUND_DELETED ||
    event?.type === EventType.INBOUND_DELETED;
  const incomingMetaErrorNotice = resolveIncomingMetaErrorNotice(
    noticeSourceOf(event),
    selectedChat?.integration,
  );
  const isUnsupportedInfoNote =
    event.message?.state === MessageState.ERROR &&
    isWapiUnsupportedInfoNote(event.message?.error);
  const isErrorBody =
    event.message?.state === MessageState.ERROR &&
    event.message?.error !== MessageErrorType.NO_ERROR &&
    !isLeadsalesTemplateRefusal(event.message?.error) &&
    !showErrorMessage(event.message?.error ?? '') &&
    !resolveOutgoingMetaErrorNotice(
      noticeSourceOf(event),
      selectedChat?.integration,
    );
  const errorEntry = isErrorBody
    ? lookupByMessageErrorType(event.message?.error ?? '')
    : undefined;
  const isInlineErrorCard = errorEntry?.displayMethod === 'chat_inline';
  const isInboundContentNotice =
    event.message?.fromMe === false &&
    isUnsupportedContentNotice(event.message?.error);

  // INT-1691: the three branches below that say the content cannot be shown
  // report it once. The INT-1704 notice shows in any state, so it is counted
  // in any state. The inline card is a failed send, and the WhatsApp API
  // incoming notice reports from MessageBubbleV2, where it is drawn.
  const showsUnsupportedNotice =
    !isDeletedMessage &&
    !incomingMetaErrorNotice &&
    (isUnsupportedInfoNote ||
      isInboundContentNotice ||
      (isErrorBody && !isInlineErrorCard));
  useUnsupportedNoticeAnalytics({
    eventId: event.id,
    chatId: event.chatID,
    createdAt: event.createdAt,
    notice: showsUnsupportedNotice ? countedNoticeOf(event.message ?? {}) : null,
  });

  // BUG-1063: whoever deleted the message, the thread says so. The lead's
  // deletion used to render nothing at all, leaving a gap the seller could not
  // tell from a message that was never sent. Which side deleted it is the only
  // information the line carries, so the two copies stay separate.
  if (isDeletedMessage) {
    const tombstoneKey =
      event.type === EventType.INBOUND_DELETED
        ? 'deletedMessage.inbound'
        : 'deletedMessage.outbound';

    return (
      <div className="flex gap-2 text-neutral-content/40">
        <NoSymbolIcon className="w-5 h-5" />
        {t(tombstoneKey, { ns: 'chat/chat' })}
      </div>
    );
  }

  // INT-1506: the lead's message did not reach us whole; the notice below it
  // explains why. A file Meta could not download stands in as a generic row
  // (WBS forwards only Meta's error text for it, never the file); a message
  // Meta did not share (131060, 131051, and the business's own echoes of them,
  // INT-1500 U4) has nothing to show. The text Meta sends in its place is its
  // own English description of the error, so it is not rendered. Checked
  // before the BUG-704 notes: in a WhatsApp API chat the notice replaces them.
  if (incomingMetaErrorNotice) {
    return incomingMetaErrorNotice.title === 'fileNotReceived' ? (
      <UnavailableFile />
    ) : (
      <></>
    );
  }

  // BUG-704: 3063 (edit) / 3064 (generic minor subtype) render as calm,
  // non-error informational notes — no orange line, no exclamation icon, no
  // "Lo sentimos" prefix. Salience floor is the SystemBubble-pill register
  // (solid gray fill + border + normal-size text + neutral icon), never the
  // 40%-opacity muted-note register (too faint — this note is the seller's only
  // signal that content was lost). Copy is direction-aware via message.fromMe.
  // Must precede the generic ERROR branch below (these codes also satisfy it).
  // INT-1500 U4: only reached while the chat's channel is unknown; a WhatsApp
  // API chat takes the notice above.
  if (isUnsupportedInfoNote) {
    const isEditNote =
      event.message?.error === MessageErrorType.WAPI_EDITED_MESSAGE_ERROR;
    const NoteIcon = isEditNote ? PencilSquareIcon : InformationCircleIcon;
    return (
      <div className="flex items-center gap-2 rounded-lg border border-black/5 bg-gray-100 px-3 py-2 text-sm text-gray-600">
        <NoteIcon className="w-4 h-4 shrink-0 text-gray-400" />
        <span>
          {renderMessageNotSupported(
            event.message?.error ?? '',
            event.message?.fromMe,
          )}
        </span>
      </div>
    );
  }

  // INT-1704: the contact sent something the chat cannot show (1004 on any
  // channel, 2001–2009 from WhatsApp Web). Same calm register as the BUG-704
  // note above, in DS tokens, with copy that says what arrived. In any state:
  // a 2001–2009 in READ would otherwise paint the provider's raw string, and
  // 1004 used to leave the body empty under an orange rail (MessageBubbleV2
  // drops that rail for these). Only an explicit `fromMe === false`: the
  // business side keeps the generic ERROR branch below. Must precede it.
  if (isInboundContentNotice) {
    return (
      <div
        data-testid="unsupported-content-notice"
        className="flex items-center gap-2 rounded-[var(--ls-radius-md)] border border-[var(--ls-border-neutral-light)] bg-[var(--ls-surface-elevation-02)] px-3 py-2 text-sm text-[var(--ls-text-neutral-default)]"
      >
        <InformationCircleIcon
          aria-hidden="true"
          className="w-4 h-4 shrink-0 text-[var(--ls-text-neutral-light)]"
        />
        <span>
          {renderMessageNotSupported(event.message?.error ?? '', false)}
        </span>
      </div>
    );
  }

  // INT-1503: a message Meta refused keeps its content; the bubble explains
  // the failure in the notice below it. INT-1500 C2: so does a send Leadsales
  // refused before Meta, which has no legacy card to show in its place.
  if (isErrorBody) {
    if (errorEntry && isInlineErrorCard) {
      return (
        <WapiInlineErrorCard
          title={t(errorEntry.titleKey, { ns: 'errors/errors' })}
          message={t(errorEntry.messageKey, { ns: 'errors/errors' })}
          code={errorEntry.code}
        />
      );
    }

    return (
      <div className="flex gap-2 italic">
        <ExclamationCircleIcon className="w-5 h-5 text-orange-400" />
        {renderMessageNotSupported(
          event.message?.error ?? '',
          event.message?.fromMe,
        )}
      </div>
    );
  }

  if (attachments?.length) {
    const attachmentContainers = attachments.map((attachment, index) => {
      const attachmentKey =
        attachment.mediaType === MediaType.LOCATION && !attachment.url
          ? `location-${attachment.latitude}-${attachment.longitude}-${index}`
          : attachment.url || `attachment-${index}`;

      return (
        <div
          key={attachmentKey}
          onClick={() => {
            if (attachment.mediaType === MediaType.LOCATION) return;
            if (attachment.mediaType === MediaType.CONTACT) return;
            // SHARE_POST cards navigate via their own "View on TikTok" CTA and
            // have no top-level url — opening the Lightbox would render a blank
            // modal on top of the new tab (INT-929).
            if (attachment.mediaType === MediaType.SHARE_POST) return;
            // BUG-1144: a document is not an image. parseSlides gives it
            // `src: ''`, so the Lightbox opened behind the download with a
            // broken <img>. FileContent owns the click for these.
            if (attachment.mediaType === MediaType.DOCUMENT) return;
            if (
              !isDocumentOrPdf(attachment?.mimeType || '') ||
              !isVideoOrAudio(attachment?.mimeType || '')
            )
              setSlideIndex(index);
            setOpenLightbox(true);
          }}
        >
          <MessageByAttachmentType
            decryptedAttachment={attachment}
            messageRef={event}
          />
          {/* One text per message, under the last attachment: inside every
              container it repeated once per card (INT-1707). */}
          {event.content?.text && index === attachments.length - 1 ? (
            <p className="text-left break-words whitespace-pre-wrap">
              {event?.createdBy === 'leadbot' ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(event?.content?.text ?? ''),
                  }}
                />
              ) : (
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(event?.content?.text ?? ''),
                  }}
                />
              )}
              {textEnd}
            </p>
          ) : null}
        </div>
      );
    });

    return (
      <>
        {attachmentContainers}
        {openLightbox && (
          <Lightbox
            attachments={attachments}
            openAt={slideIndex}
            isLightboxOpen={openLightbox}
            onClose={() => setOpenLightbox(false)}
          />
        )}
      </>
    );
  }

  if (!isEmpty(event?.content?.text)) {
    return (
      <p className="break-words">
        {event?.createdBy === 'leadbot' ? (
          <>
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(event?.content?.text ?? ''),
              }}
            />
            {textEnd}
          </>
        ) : (
          renderTextWithNewlinesAndLinks(event?.content?.text ?? '', textEnd)
        )}
      </p>
    );
  }

  if (event?.message?.state === MessageState.DECRYPTING) {
    return <EncryptedContent />;
  }

  return <></>;
}
